/**
 * RFQ Create Wizard - CR-1403
 *
 * 6-step wizard for creating RFQs:
 * 1. Basic Details
 * 2. Line Items
 * 3. Additional Costs
 * 4. Documents & NDA
 * 5. Supplier Selection
 * 6. Review & Send
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI, suppliersAPI } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';

import {
  STEPS,
  EMPTY_LINE_ITEM,
  EMPTY_COST,
  EMPTY_CERTIFICATE,
  DEFAULT_BASIC_DETAILS,
} from './rfqWizardTypes';
import type {
  BasicDetailsForm,
  LineItemForm,
  AdditionalCostForm,
  RequiredCertificateForm,
  SupplierOption,
} from './rfqWizardTypes';
import {
  BasicDetailsStep,
  LineItemsStep,
  AdditionalCostsStep,
  DocumentsStep,
  SupplierSelectionStep,
  ReviewStep,
} from './RFQWizardSteps';

interface RFQCreateWizardProps {
  onClose: () => void;
  onCreate?: () => void;
}

export default function RFQCreateWizard({ onClose, onCreate }: RFQCreateWizardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);

  // Step 1: Basic Details
  const [basicDetails, setBasicDetails] = useState<BasicDetailsForm>({ ...DEFAULT_BASIC_DETAILS });

  // Step 2: Line Items
  const [lineItems, setLineItems] = useState<LineItemForm[]>([{ ...EMPTY_LINE_ITEM }]);

  // Step 3: Additional Costs
  const [additionalCosts, setAdditionalCosts] = useState<AdditionalCostForm[]>([]);

  // Step 4: Documents & Certificates
  const [requiredCertificates, setRequiredCertificates] = useState<RequiredCertificateForm[]>([]);

  // Step 5: Selected Suppliers
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await suppliersAPI.getProfiles({ page_size: 1000 });
      setSuppliers(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const calculateTotalValue = () => {
    const lineItemsTotal = lineItems.reduce((sum, item) => {
      const price = item.target_unit_price || 0;
      return sum + (price * item.quantity);
    }, 0);
    const costsTotal = additionalCosts.reduce((sum, cost) => sum + cost.amount, 0);
    return lineItemsTotal + costsTotal;
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(basicDetails.title && basicDetails.description && basicDetails.response_deadline);
      case 2:
        return lineItems.length > 0 && lineItems.every(item =>
          item.item_description && item.part_number && item.quantity > 0
        );
      case 3:
      case 4:
      case 5:
      case 6:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const updateLineItem = (index: number, field: keyof LineItemForm, value: string | number | null) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const updateAdditionalCost = (index: number, field: keyof AdditionalCostForm, value: string | number) => {
    const updated = [...additionalCosts];
    updated[index] = { ...updated[index], [field]: value };
    setAdditionalCosts(updated);
  };

  const updateCertificate = (index: number, field: keyof RequiredCertificateForm, value: string | number | boolean) => {
    const updated = [...requiredCertificates];
    updated[index] = { ...updated[index], [field]: value };
    setRequiredCertificates(updated);
  };

  const toggleSupplier = (companyId: number) => {
    setSelectedSuppliers(prev =>
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const handleSubmit = async (sendToSuppliers: boolean = false) => {
    try {
      setLoading(true);

      if (!user?.company) {
        toast.error('User company not found. Please log in again.');
        return;
      }

      // CR-5468: don't ship customer in the body. The backend now stamps
      // it from the requester's active company context (customer-portal
      // users) so we don't send a wrong-typed FK and 400 with the
      // impossible-to-action "Invalid pk" / generic "Failed to create
      // RFQ" message that Sarah reported. Helix admins still need to
      // pick a customer, but that path doesn't go through this wizard.
      const rfqData = {
        title: basicDetails.title,
        description: basicDetails.description,
        category: basicDetails.category,
        priority: basicDetails.priority,
        currency: basicDetails.currency,
        response_deadline: basicDetails.response_deadline,
        validity_period_days: basicDetails.validity_period_days,
        contract_type: basicDetails.contract_type,
        contract_frequency: basicDetails.contract_type === 'regular_running' ? basicDetails.contract_frequency || undefined : undefined,
        terms_and_conditions: basicDetails.terms_and_conditions,
        special_requirements: basicDetails.special_requirements,
        nda_required: basicDetails.nda_required,
        auto_create_contract: basicDetails.auto_create_contract,
      };

      const rfqResponse = await rfqAPI.createRFQ(rfqData);
      const rfqId = rfqResponse.data.id;

      for (let i = 0; i < lineItems.length; i++) {
        const item = lineItems[i];
        await rfqAPI.createLineItem({
          rfq: rfqId,
          line_number: i + 1,
          item_description: item.item_description,
          part_number: item.part_number,
          quantity: item.quantity,
          unit_of_measure: item.unit_of_measure,
          target_unit_price: item.target_unit_price || undefined,
          // CR-5467: drawing_issue is the new line-level revision field;
          // delivery_location / required_delivery_date are intentionally
          // not sent — they're moving to the RFQ header in a follow-up.
          drawing_issue: item.drawing_issue,
          technical_specifications: item.technical_specifications,
          notes: item.notes,
        });
      }

      for (const cost of additionalCosts) {
        await rfqAPI.createAdditionalCost({
          rfq: rfqId,
          cost_type: cost.cost_type,
          description: cost.description,
          amount: cost.amount,
          currency: basicDetails.currency,
          notes: cost.notes,
        });
      }

      for (const cert of requiredCertificates) {
        await rfqAPI.createRequiredCertificate({
          rfq: rfqId,
          certificate_type: cert.certificate_type,
          certificate_name: cert.certificate_name,
          description: cert.description,
          is_mandatory: cert.is_mandatory,
          expiry_tolerance_days: cert.expiry_tolerance_days,
        });
      }

      if (sendToSuppliers && selectedSuppliers.length > 0) {
        await rfqAPI.sendToSuppliers(rfqId, { supplier_ids: selectedSuppliers });
        toast.success('RFQ created and sent to suppliers');
      } else {
        toast.success('RFQ created as draft');
      }

      onCreate?.();
      onClose();
      navigate(`/rfq/${rfqId}`);
    } catch (error) {
      console.error('Error creating RFQ:', error);
      // CR-5468: surface the backend's actual validation message so users
      // get an actionable error instead of the generic "Failed to create
      // RFQ" toast that hid the real cause for weeks.
      const msg = getApiErrorMessage(error, 'Failed to create RFQ');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {STEPS.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep > step.id
                  ? 'bg-primary text-primary-foreground'
                  : currentStep === step.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {currentStep > step.id ? <Check className="h-5 w-5" /> : step.id}
            </div>
            <span className="text-xs mt-1 text-center max-w-[80px]">{step.title}</span>
          </div>
          {index < STEPS.length - 1 && (
            <div
              className={`h-0.5 w-12 mx-2 ${
                currentStep > step.id ? 'bg-primary' : 'bg-muted'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <BasicDetailsStep basicDetails={basicDetails} setBasicDetails={setBasicDetails} />;
      case 2:
        return (
          <LineItemsStep
            lineItems={lineItems}
            onAdd={() => setLineItems([...lineItems, { ...EMPTY_LINE_ITEM }])}
            onRemove={(index) => { if (lineItems.length > 1) setLineItems(lineItems.filter((_, i) => i !== index)); }}
            onUpdate={updateLineItem}
          />
        );
      case 3:
        return (
          <AdditionalCostsStep
            costs={additionalCosts}
            currency={basicDetails.currency}
            onAdd={() => setAdditionalCosts([...additionalCosts, { ...EMPTY_COST }])}
            onRemove={(index) => setAdditionalCosts(additionalCosts.filter((_, i) => i !== index))}
            onUpdate={updateAdditionalCost}
          />
        );
      case 4:
        return (
          <DocumentsStep
            certificates={requiredCertificates}
            ndaRequired={basicDetails.nda_required}
            onAdd={() => setRequiredCertificates([...requiredCertificates, { ...EMPTY_CERTIFICATE }])}
            onRemove={(index) => setRequiredCertificates(requiredCertificates.filter((_, i) => i !== index))}
            onUpdate={updateCertificate}
          />
        );
      case 5:
        return (
          <SupplierSelectionStep
            suppliers={suppliers}
            selectedSuppliers={selectedSuppliers}
            onToggle={toggleSupplier}
          />
        );
      case 6:
        return (
          <ReviewStep
            basicDetails={basicDetails}
            lineItems={lineItems}
            additionalCosts={additionalCosts}
            requiredCertificates={requiredCertificates}
            selectedSuppliers={selectedSuppliers}
            suppliers={suppliers}
            totalValue={calculateTotalValue()}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-background min-h-[calc(100vh-200px)]">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Create New RFQ</h1>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {renderStepIndicator()}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{STEPS[currentStep - 1].title}</CardTitle>
            <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderCurrentStep()}
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={currentStep === 1 ? onClose : handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>

          <div className="flex gap-2">
            {currentStep === 6 ? (
              <>
                <Button variant="outline" onClick={() => handleSubmit(false)} disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save as Draft
                </Button>
                {selectedSuppliers.length > 0 && (
                  <Button onClick={() => handleSubmit(true)} disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create & Send
                  </Button>
                )}
              </>
            ) : (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
